import posthog from "posthog-js";

// Vite env vars baked in at build time (.env.production, .env.dev). With
// VITE_POSTHOG_KEY empty, PostHog is never initialized.
const key = import.meta.env.VITE_POSTHOG_KEY;
const host = import.meta.env.VITE_POSTHOG_HOST;
const environment = import.meta.env.VITE_POSTHOG_ENVIRONMENT;

let initialized = false;
let consented = false;
let userId: string | null = null;

function initAnalytics(): void {
   if (!key || !host || initialized) return;
   posthog.init(key, {
      api_host: host,
      capture_pageview: "history_change",
      autocapture: false,
      disable_session_recording: true,
      person_profiles: "identified_only",
      before_send: (event) => {
         if (!event) return event;
         return {
            ...event,
            properties: { ...event.properties, service: "frontend", environment },
         };
      },
   });
   initialized = true;
   if (userId) posthog.identify(userId);
}

function clearAnalyticsStorage(): void {
   const name = `ph_${key}_posthog`;
   try {
      localStorage.removeItem(name);
   } catch {
      // Ignore.
   }
   document.cookie = `${name}=; Max-Age=0; path=/`;
   const domain = location.hostname.split(".").slice(-2).join(".");
   document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}`;
}

/**
 * Grants or withdraws consent to analytics for this browser. PostHog is loaded
 * on the first grant and sets no cookie or storage before it; withdrawing opts
 * out and deletes PostHog's stored identity.
 */
export function setAnalyticsConsent(granted: boolean): void {
   consented = granted;
   if (granted) {
      if (!initialized) {
         initAnalytics();
      } else {
         posthog.opt_in_capturing();
      }
   } else if (initialized) {
      posthog.opt_out_capturing();
      clearAnalyticsStorage();
   }
}

/**
 * Attributes events to the signed-in user, now or once consent is granted.
 * Pass the user's UUID, never an email or name.
 */
export function identifyAnalyticsUser(id: string): void {
   userId = id;
   if (initialized && consented) posthog.identify(id);
}

/**
 * Forgets the signed-in user on sign-out. The browser's consent choice is kept.
 */
export function resetAnalyticsUser(): void {
   userId = null;
   if (!initialized) return;
   posthog.reset();
   if (consented) posthog.opt_in_capturing();
}
